import { useCallback, useMemo } from 'react';
import { MinRowsForUsingVirtuoso } from 'src/components/Accordions';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { useSingleTimePeriodPurchasesQuery } from 'src/contexts/PurchaseDataContext/useSingleTimePeriodPurchaseQuery';
import {
  useUserHasFeature,
  useUserHasTableTotalsFeature,
} from 'src/hooks/useUserHasFeature';
import { PurchaseOrderTable } from 'src/tables/PurchaseTable';
import { PurchaseOrderTableV2 } from 'src/tables/PurchaseTable/PurchaseTableV2';
import { createAggregatedPurchaseOrderTicketGroup } from 'src/utils/purchaseUtils';
import {
  ActionOutboxEntityType,
  Feature,
  PurchaseOrder,
  PurchaseOrderQuery,
  PurchaseOrderTicketGroup,
  TimePeriodContainingPurchaseOrderResult,
} from 'src/WebApiController';

type VirtualizedPurchaseAccordionItemProps =
  TimePeriodContainingPurchaseOrderResult & {
    isExpanded: boolean;
  };

export function PurchaseDateItemBody({
  firstOfTimePeriod,
  isExpanded,
  ...rest
}: VirtualizedPurchaseAccordionItemProps) {
  const { filterQuery } = useFilterQueryContext<PurchaseOrderQuery>();

  const hasTableTotalsFeature = useUserHasTableTotalsFeature(
    ActionOutboxEntityType.Purchase
  );
  const hasPurchaseTableV2Feature = useUserHasFeature(Feature.PurchaseTableV2);

  const filterPurchaseOrders = useCallback(
    (purchaseOrders: PurchaseOrder[] | null | undefined) => {
      if (purchaseOrders && filterQuery.purchaseOrderState) {
        return purchaseOrders.filter(
          (po) => po.poState === filterQuery.purchaseOrderState
        );
      }

      return purchaseOrders;
    },
    [filterQuery.purchaseOrderState]
  );

  const { singleTimePeriodPurchaseQuery } = useSingleTimePeriodPurchasesQuery(
    firstOfTimePeriod,
    true // isExpanded should be true - as long as this is rendered, it is expanded. Otherwise UI will be laggy
  );

  const purchaseOrders = useMemo(
    () => filterPurchaseOrders(singleTimePeriodPurchaseQuery.data?.items),
    [filterPurchaseOrders, singleTimePeriodPurchaseQuery.data]
  );

  const entities = useMemo(() => {
    return purchaseOrders?.map((p) => p as PurchaseOrderTicketGroup) ?? [];
  }, [purchaseOrders]);

  const entitiesSummary = useMemo(() => {
    if (!hasTableTotalsFeature) {
      return null;
    }

    if (entities?.length != singleTimePeriodPurchaseQuery.data?.items?.length) {
      return createAggregatedPurchaseOrderTicketGroup(entities);
    }

    return singleTimePeriodPurchaseQuery.data?.summary;
  }, [
    entities,
    hasTableTotalsFeature,
    singleTimePeriodPurchaseQuery.data?.items?.length,
    singleTimePeriodPurchaseQuery.data?.summary,
  ]);

  const usingVirtuoso =
    (purchaseOrders?.length ?? rest.purchaseCount) > MinRowsForUsingVirtuoso;

  return hasPurchaseTableV2Feature ? (
    <PurchaseOrderTableV2
      getDataFail={Boolean(
        singleTimePeriodPurchaseQuery.isError ||
          singleTimePeriodPurchaseQuery.failureReason
      )}
      groupId={firstOfTimePeriod}
      entities={entities}
      entitiesSummary={entitiesSummary}
      entityCount={rest.purchaseCount}
      useVirtuoso={usingVirtuoso}
      disablePagination
      enableColumnFilters
    />
  ) : (
    <PurchaseOrderTable
      getDataFail={Boolean(
        singleTimePeriodPurchaseQuery.isError ||
          singleTimePeriodPurchaseQuery.failureReason
      )}
      groupId={firstOfTimePeriod}
      entities={entities}
      entitiesSummary={entitiesSummary}
      entityCount={rest.purchaseCount}
      useVirtuoso={usingVirtuoso}
      disablePagination
      enableColumnFilters
    />
  );
}
